import React, { useMemo, useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Modal } from "react-bootstrap";
import { LoadingSpinner } from "../components/loading-spinner";

// Register chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Chart options for stacked bar chart - UPDATED FOR HORIZONTAL SCROLL
const getChartOptions = (dataLength = 0) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "nearest",
    intersect: false,
  },
  plugins: {
    legend: {
      position: "top",
      labels: {
        boxWidth: 12,
        padding: 15,
      },
      onClick: (e, legendItem, legend) => {
        const index = legendItem.datasetIndex;
        const chart = legend.chart;

        const visibleCount = chart.data.datasets.filter((dataset, i) =>
          chart.isDatasetVisible(i)
        ).length;

        if (visibleCount === 1 && chart.isDatasetVisible(index)) return;

        const meta = chart.getDatasetMeta(index);
        meta.hidden =
          meta.hidden === null ? !chart.data.datasets[index].hidden : null;
        chart.update();
      },
    },
    tooltip: {
      enabled: true, // Enable tooltip for better mobile experience
      mode: "index",
      intersect: false,
    },
  },
  scales: {
    x: {
      stacked: false,
      grid: { display: false },
      ticks: {
        autoSkip: false,
        maxRotation: 45, // Allow rotation for better readability
        minRotation: 0,
        // Calculate minimum width for bars based on data length
        min: 0,
        max: dataLength > 15 ? dataLength - 1 : undefined, // Show all labels if less than 15, otherwise enable scroll
      },
      // Add bar thickness and category percentage for better spacing
      barThickness: dataLength > 20 ? "flex" : 20, // Flexible thickness when many bars
      categoryPercentage: dataLength > 15 ? 0.6 : 0.8, // Reduce category width when many bars
      barPercentage: dataLength > 15 ? 0.8 : 0.9, // Reduce bar width when many bars
    },
    y: {
      stacked: false,
      beginAtZero: true,
      suggestedMax: (context) => {
        const chart = context.chart;
        const datasets = chart.data.datasets;
        let maxValue = 0;
        datasets.forEach((dataset) => {
          const datasetMax = Math.max(...dataset.data);
          maxValue = Math.max(maxValue, datasetMax);
        });
        return maxValue + 2;
      },
      ticks: {
        callback: (value) => `${value}h`,
      },
      grid: { color: "#f0f0f0" },
    },
  },
  // Add layout padding to ensure labels aren't cut off
  layout: {
    padding: {
      left: 10,
      right: 10,
      top: 10,
      bottom: 10,
    },
  },
});

const customDataLabelsPlugin = {
  id: "customDataLabels",
  afterDraw: function (chart) {
    const ctx = chart.ctx;
    const datasets = chart.data.datasets;

    chart.data.labels.forEach((label, index) => {
      datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        const bar = meta.data[index];
        const currentValue = dataset.data[index];

        if (bar && currentValue >= 0 && !meta.hidden) {
          ctx.save();
          ctx.fillStyle = "#333";
          ctx.font = "bold 14px Arial";
          ctx.textAlign = "center";
          ctx.fillText(`${currentValue}h`, bar.x, bar.y - 5);
          ctx.restore();
        }
      });
    });
  },
};

// Helper function to safely parse dates
const safeParseDate = (dateString) => {
  if (!dateString) return null;
  // const date = new Date(dateString);
  // return isNaN(date.getTime()) ? null : date;

  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

// Helper function to safely format date to ISO string
const safeToISOString = (date) => {
  if (!date || isNaN(date.getTime())) return null;
  return date.toISOString().split("T")[0];
};

const StackbarcartWorkhours = ({
  data,
  detailsData,
  isLoading,
  isDetailsLoading,
  isError,
  isDetailsError,
  onFilterChange,
  onBarClick, // Callback when bar is clicked
  showModalOnClick = true, // NEW PROP: default true for dashboard, false for VolunteerHours
}) => {
  const [filter, setFilter] = useState("last7Days");
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Search functionality
  const [searchTerm, setSearchTerm] = useState("");

  // Modal filter states
  const [modalFilter, setModalFilter] = useState("single_day");
  const [modalCustomDateRange, setModalCustomDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [showModalCustomDatePicker, setShowModalCustomDatePicker] =
    useState(false);
  const [modalDateRange, setModalDateRange] = useState(null);

  const itemsPerPage = 10;

  // Function to calculate date range based on filter
  const getDateRangeForFilter = (
    filterType,
    customRange = null,
    clickedDate = null
  ) => {
    const today = new Date();
    let startDate, endDate;

    switch (filterType) {
      case "single_day":
        if (clickedDate) {
          return {
            startDate: clickedDate,
            endDate: clickedDate,
          };
        }
        return null;
      case "last7Days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        endDate = today;
        break;
      case "month1":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1);
        endDate = today;
        break;
      case "month3":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 3);
        endDate = today;
        break;
      case "custom":
        if (customRange && customRange.startDate && customRange.endDate) {
          return {
            startDate: customRange.startDate,
            endDate: customRange.endDate,
          };
        }
        return null;
      default:
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        endDate = today;
    }

    return {
      startDate: safeToISOString(startDate),
      endDate: safeToISOString(endDate),
    };
  };

  // Process data for stacked bar chart
  const processDataForChart = (rawData) => {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return { dates: [], staffData: [], volunteerData: [] };
    }

    const validData = rawData.filter((item) => item.date);
    const sortedData = validData.sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    const dates = sortedData.map((item) => item.date);
    const staffData = sortedData.map((item) => item.staff_hours || 0);
    const volunteerData = sortedData.map((item) => item.volunteer_hours || 0);

    return { dates, staffData, volunteerData };
  };

  // Flatten the nested API response for modal details
  const flattenDetailsData = (nestedData) => {
    if (!nestedData || !Array.isArray(nestedData)) {
      return [];
    }

    const flattened = [];

    nestedData.forEach((person) => {
      if (person.daily_hours && Array.isArray(person.daily_hours)) {
        person.daily_hours.forEach((dayEntry) => {
          if (!dayEntry.date || isNaN(new Date(dayEntry.date).getTime())) {
            return;
          }

          flattened.push({
            date: dayEntry.date,
            hours: dayEntry.hours || 0,
            total_hours: dayEntry.hours || 0,
            name: person.name,
            agent_name: person.name,
            email: person.email,
            department: person.department,
            user_type: person.department,
            type: person.department,
            organization_name: person.organization_name,
            organization: person.organization_name,
            skills: person.skills,
          });
        });
      }
    });

    return flattened;
  };

  const chartData = useMemo(() => {
    if (!data) {
      return { labels: [], datasets: [], originalDates: [] };
    }

    const { dates, staffData, volunteerData } = processDataForChart(data);

    const formattedLabels = dates.map((date) => {
      const dateObj = safeParseDate(date);
      if (!dateObj) return "Invalid Date";

      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    });

    const datasets = [
      {
        label: "Staff",
        data: staffData,
        backgroundColor: "#2cb3bf",
        borderColor: "#2cb3bf",
        borderWidth: 1,
        borderRadius: 5,
      },
      {
        label: "Volunteer",
        data: volunteerData,
        backgroundColor: "#ff7234",
        borderColor: "#ff7234",
        borderWidth: 1,
        borderRadius: 5,
      },
    ];

    return {
      labels: formattedLabels,
      datasets,
      originalDates: dates,
    };
  }, [data]);

  // Calculate dynamic chart options based on data length
  const chartOptions = useMemo(() => {
    const dataLength = chartData.labels.length;
    return getChartOptions(dataLength);
  }, [chartData.labels.length]);

  // Process details data - filter based on modal date range, selected type, and search term
  const filteredModalDetails = useMemo(() => {
    if (
      !detailsData ||
      !Array.isArray(detailsData) ||
      !selectedType ||
      !modalDateRange
    ) {
      return [];
    }

    const flattenedData = flattenDetailsData(detailsData);

    let filtered = flattenedData.filter((item) => {
      const itemDate = safeParseDate(item.date);
      if (!itemDate) return false;

      const itemDateString = safeToISOString(itemDate);

      const isWithinDateRange =
        itemDateString >= modalDateRange.startDate &&
        itemDateString <= modalDateRange.endDate;

      const matchesType =
        selectedType === "staff"
          ? item.department.toLowerCase() === "staff" ||
            item.type.toLowerCase() === "staff" ||
            item.user_type.toLowerCase() === "staff"
          : item.department.toLowerCase() === "volunteer" ||
            item.type.toLowerCase() === "volunteer" ||
            item.user_type.toLowerCase() === "volunteer";

      return isWithinDateRange && matchesType;
    });

    if (searchTerm.trim()) {
      const searchTermLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const name = (item.agent_name || item.name || "").toLowerCase();
        const email = (item.email || "").toLowerCase();
        const organization = (
          item.organization_name ||
          item.organization ||
          ""
        ).toLowerCase();
        const date =
          safeParseDate(item.date)?.toLocaleDateString().toLowerCase() || "";

        return (
          name.includes(searchTermLower) ||
          email.includes(searchTermLower) ||
          organization.includes(searchTermLower) ||
          date.includes(searchTermLower)
        );
      });
    }

    return filtered;
  }, [detailsData, selectedType, modalDateRange, searchTerm]);

  // UPDATED: Handle bar click - open modal only if showModalOnClick is true
  const handleBarClick = (event, elements, chart) => {
    if (!elements?.length) return;

    const datasetIndex = elements[0].datasetIndex;
    const dateIndex = elements[0].index;
    const type = chartData.datasets[datasetIndex].label?.toLowerCase();
    const clickedDate = chartData.originalDates[dateIndex];

    setSelectedType(type);
    setSelectedDate(clickedDate);
    setCurrentPage(1);
    setSearchTerm("");

    setModalFilter("single_day");

    const dateRangeForModal = getDateRangeForFilter(
      "single_day",
      null,
      clickedDate
    );

    setModalDateRange(dateRangeForModal);

    if (onBarClick && dateRangeForModal) {
      onBarClick(type, clickedDate, dateRangeForModal);
    }

    // Only show modal if showModalOnClick is true (dashboard page)
    if (showModalOnClick) {
      setShowModal(true);
    }
  };

  // Handle modal filter changes
  const handleModalFilterChange = (newFilter) => {
    if (newFilter === "custom") {
      setShowModalCustomDatePicker(true);
      return;
    }

    setModalFilter(newFilter);
    setShowModalCustomDatePicker(false);

    let dateRange;
    if (newFilter === "single_day") {
      dateRange = getDateRangeForFilter("single_day", null, selectedDate);
    } else {
      dateRange = getDateRangeForFilter(newFilter);
    }

    if (dateRange) {
      setModalDateRange(dateRange);

      if (onBarClick && selectedType && selectedDate) {
        onBarClick(selectedType, selectedDate, dateRange);
      }
    }
  };

  // Handle custom date filter for modal
  const handleModalCustomDateFilter = async () => {
    if (modalCustomDateRange.startDate && modalCustomDateRange.endDate) {
      const startDate = safeParseDate(modalCustomDateRange.startDate);
      const endDate = safeParseDate(modalCustomDateRange.endDate);

      if (!startDate || !endDate) {
        alert("Invalid date format");
        return;
      }

      if (startDate > endDate) {
        alert("Start date cannot be later than end date");
        return;
      }

      const dateRange = getDateRangeForFilter("custom", modalCustomDateRange);
      if (dateRange) {
        setModalDateRange(dateRange);

        if (onBarClick && selectedType && selectedDate) {
          onBarClick(selectedType, selectedDate, dateRange);
        }
      }

      setShowModalCustomDatePicker(false);
      setModalFilter("custom");
    }
  };

  // Handle custom date filter for main chart
  const handleCustomDateFilter = async () => {
    if (customDateRange.startDate && customDateRange.endDate) {
      const startDate = safeParseDate(customDateRange.startDate);
      const endDate = safeParseDate(customDateRange.endDate);

      if (!startDate || !endDate) {
        alert("Invalid date format");
        return;
      }

      if (startDate > endDate) {
        alert("Start date cannot be later than end date");
        return;
      }

      const dateRange = getDateRangeForFilter("custom", customDateRange);
      if (onFilterChange && dateRange) {
        onFilterChange(dateRange);
      }

      setShowCustomDatePicker(false);
      setFilter("custom");
    }
  };

  // Reset currentPage when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Pagination for modal
  const paginated = filteredModalDetails?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(
    (filteredModalDetails?.length || 0) / itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle main chart filter change
  const handleFilterChange = (newFilter) => {
    if (newFilter === "custom") {
      setShowCustomDatePicker(true);
      return;
    }

    setFilter(newFilter);
    setShowCustomDatePicker(false);

    const dateRange = getDateRangeForFilter(newFilter);
    if (onFilterChange && dateRange) {
      onFilterChange(dateRange);
    }
  };

  // Reset modal states when closing modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedType(null);
    setSelectedDate(null);
    setModalFilter("single_day");
    setModalCustomDateRange({ startDate: "", endDate: "" });
    setShowModalCustomDatePicker(false);
    setModalDateRange(null);
    setCurrentPage(1);
    setSearchTerm("");
  };

  // Clear search function
  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  if (isError) {
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "#dc3545" }}>
        <p>Error loading work hours data</p>
      </div>
    );
  }

  return (
    <>
      <FilterWrapper>
        {[
          { key: "last7Days", label: "Last 7 Days" },
          { key: "month1", label: "1 Month" },
          { key: "month3", label: "3 Months" },
          { key: "custom", label: "Custom" },
        ].map((filterOption) => (
          <FilterButton
            key={filterOption.key}
            active={filter === filterOption.key}
            onClick={() => handleFilterChange(filterOption.key)}
            disabled={isLoading}
          >
            {filterOption.label}
          </FilterButton>
        ))}
      </FilterWrapper>

      {showCustomDatePicker && (
        <CustomDateWrapper>
          <DateInputGroup>
            <div>
              <label>From:</label>
              <input
                type="date"
                value={customDateRange.startDate}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <label>To:</label>
              <input
                type="date"
                value={customDateRange.endDate}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({
                    ...prev,
                    endDate: e.target.value,
                  }))
                }
              />
            </div>
            <FilterButton
              onClick={handleCustomDateFilter}
              style={{ backgroundColor: "#2cb3bf", color: "white" }}
              disabled={
                !customDateRange.startDate ||
                !customDateRange.endDate ||
                isLoading
              }
            >
              {isLoading ? "Loading..." : "Apply"}
            </FilterButton>
            <FilterButton
              onClick={() => {
                setShowCustomDatePicker(false);
                setCustomDateRange({ startDate: "", endDate: "" });
              }}
              style={{ backgroundColor: "#ff7234", color: "white" }}
              disabled={isLoading}
            >
              Cancel
            </FilterButton>
          </DateInputGroup>
        </CustomDateWrapper>
      )}

      {isLoading ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <LoadingSpinner />
        </div>
      ) : (
        <ChartsWrapper className="row m-3">
          <ChartScrollWrapper className="col-md-12 col-sm-12">
            <ChartCanvasWrapper dataLength={chartData.labels.length}>
              <Bar
                data={chartData}
                options={{
                  ...chartOptions,
                  interaction: {
                    mode: "nearest",
                    intersect: true,
                  },
                  onClick: (event, elements, chart) => {
                    if (elements && elements.length > 0) {
                      handleBarClick(event, elements, chart);
                    }
                  },
                  plugins: {
                    ...chartOptions.plugins,
                    title: {
                      display: true,
                      position: "bottom",
                      font: { size: 16 },
                    },
                  },
                }}
                plugins={[customDataLabelsPlugin]}
              />
            </ChartCanvasWrapper>
          </ChartScrollWrapper>
        </ChartsWrapper>
      )}

      {/* Only show modal if showModalOnClick is true (dashboard page) */}
      {showModalOnClick && (
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          centered
          size="xl"
          backdrop="static"
          dialogClassName="custom-modal"
        >
          <StyledModalContent>
            <StyledModalHeader>
              <h5>
                {selectedType &&
                  selectedType.charAt(0).toUpperCase() + selectedType.slice(1)}
                {" ("}
                {modalDateRange && (
                  <>
                    {modalFilter === "single_day" ? (
                      safeParseDate(selectedDate)?.toLocaleDateString()
                    ) : (
                      <>
                        {safeParseDate(
                          modalDateRange.startDate
                        )?.toLocaleDateString()}{" "}
                        -{" "}
                        {safeParseDate(
                          modalDateRange.endDate
                        )?.toLocaleDateString()}
                      </>
                    )}
                  </>
                )}
                {")"}
              </h5>
              <button onClick={handleCloseModal}>&times;</button>
            </StyledModalHeader>

            <StyledModalBody>
              <ModalFilterWrapper>
                <h6>Filters:</h6>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginBottom: "15px",
                  }}
                >
                  {[
                    { key: "single_day", label: "Selected Date Only" },
                    { key: "last7Days", label: "Last 7 Days" },
                    { key: "month1", label: "1 Month" },
                    { key: "month3", label: "3 Months" },
                    { key: "custom", label: "Custom" },
                  ].map((filterOption) => (
                    <FilterButton
                      key={filterOption.key}
                      active={modalFilter === filterOption.key}
                      onClick={() => handleModalFilterChange(filterOption.key)}
                      disabled={isDetailsLoading}
                      style={{ fontSize: "12px", padding: "5px 10px" }}
                    >
                      {filterOption.label}
                    </FilterButton>
                  ))}
                </div>

                {showModalCustomDatePicker && (
                  <CustomDateWrapper style={{ marginBottom: "15px" }}>
                    <DateInputGroup>
                      <div>
                        <label>From:</label>
                        <input
                          type="date"
                          value={modalCustomDateRange.startDate}
                          onChange={(e) =>
                            setModalCustomDateRange((prev) => ({
                              ...prev,
                              startDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label>To:</label>
                        <input
                          type="date"
                          value={modalCustomDateRange.endDate}
                          onChange={(e) =>
                            setModalCustomDateRange((prev) => ({
                              ...prev,
                              endDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <FilterButton
                        onClick={handleModalCustomDateFilter}
                        style={{ backgroundColor: "#17a2b8", color: "white" }}
                        disabled={
                          !modalCustomDateRange.startDate ||
                          !modalCustomDateRange.endDate ||
                          isDetailsLoading
                        }
                      >
                        {isDetailsLoading ? "Loading..." : "Apply"}
                      </FilterButton>
                      <FilterButton
                        onClick={() => {
                          setShowModalCustomDatePicker(false);
                          setModalCustomDateRange({
                            startDate: "",
                            endDate: "",
                          });
                        }}
                        style={{ backgroundColor: "#ff7234", color: "white" }}
                        disabled={isDetailsLoading}
                      >
                        Cancel
                      </FilterButton>
                    </DateInputGroup>
                  </CustomDateWrapper>
                )}
              </ModalFilterWrapper>

              <SearchWrapper>
                <SearchInputContainer>
                  <SearchInput
                    type="text"
                    placeholder="Search by name, email, organization, or date..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <ClearSearchButton onClick={clearSearch}>
                      ✕
                    </ClearSearchButton>
                  )}
                </SearchInputContainer>
              </SearchWrapper>

              {isDetailsLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <LoadingSpinner />
                </div>
              ) : (
                <>
                  <ResultsInfo>
                    Showing {filteredModalDetails?.length || 0} results
                    {searchTerm && ` for "${searchTerm}"`}
                  </ResultsInfo>

                  <TableContainer>
                    <StyledTable>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Name</th>
                          <th>Email</th>
                          {selectedType?.toLowerCase() === "volunteer" && (
                            <th>Organization</th>
                          )}
                          <th>Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated?.length > 0 ? (
                          paginated.map((item, index) => (
                            <tr key={index}>
                              <td>
                                {safeParseDate(
                                  item.date
                                )?.toLocaleDateString() || "N/A"}
                              </td>
                              <td>{item.agent_name || item.name || "N/A"}</td>
                              <td>{item.email || "N/A"}</td>
                              {selectedType?.toLowerCase() === "volunteer" && (
                                <td>
                                  {item.organization_name ||
                                    item.organization ||
                                    "N/A"}
                                </td>
                              )}

                              <td>
                                <HoursBadge>
                                  {item.total_hours || item.hours || 0}h
                                </HoursBadge>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="6"
                              style={{ textAlign: "center", padding: "20px" }}
                            >
                              {searchTerm
                                ? `No results found for "${searchTerm}"`
                                : "No data available for the selected date range"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </StyledTable>
                  </TableContainer>

                  {totalPages > 1 && (
                    <StyledPagination>
                      <PaginationContainer>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>

                        {Array.from(
                          { length: totalPages },
                          (_, i) => i + 1
                        ).map((page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={currentPage === page ? "active" : ""}
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </PaginationContainer>
                    </StyledPagination>
                  )}
                </>
              )}
            </StyledModalBody>

            <StyledModalFooter>
              <button onClick={handleCloseModal}>Close</button>
            </StyledModalFooter>
          </StyledModalContent>
        </Modal>
      )}
    </>
  );
};

export default StackbarcartWorkhours;

// UPDATED Styled Components for better horizontal scrolling
const ChartsWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-around;
`;

const ChartScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  scroll-behavior: smooth;
  white-space: nowrap;
`;

const ChartCanvasWrapper = styled.div`
  height: 400px;
  /* ✅ Ensure min width gives enough space per bar */
  min-width: ${({ dataLength }) => `${Math.max(dataLength * 70, 800)}px`};
  padding: 20px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;

  /* ✅ Prevent Chart.js canvas from shrinking */
  canvas {
    width: 100% !important;
    height: 100% !important;
  }

  @media (max-width: 768px) {
    min-width: ${({ dataLength }) => `${Math.max(dataLength * 60, 600)}px`};
    padding: 10px;
  }
`;

const FilterWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 15px 0;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 4px 16px;
  border-radius: 8px;
  border: 1px solid #106271;
  background-color: ${({ active }) => (active ? "#106271" : "transparent")};
  color: ${({ active }) => (active ? "#ffffff" : "#106271")};
  font-weight: ${({ active }) => (active ? "500" : "500")};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Futura PT", sans-serif;
  letter-spacing: 0.15em;

  &:hover:not(:disabled) {
    border: 1px solid #ec3310;
    background-color: ${({ active }) => (active ? "#106271" : "transparent")};
    color: ${({ active }) => (active ? "#ffffff" : "#ec3310")};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CustomDateWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 15px 0;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
`;

const DateInputGroup = styled.div`
  display: flex;
  gap: 15px;
  align-items: end;
  flex-wrap: wrap;

  div {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
  }

  input[type="date"] {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StyledModalContent = styled.div`
  border-radius: 20px;
  background: white;
  box-shadow: 0 15px 25px rgba(0, 0, 0, 0.1);
  padding: 20px 10px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const StyledModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 30px 0 30px;
  flex-shrink: 0;
  h5 {
    font-weight: 600;
    color: #178b96;
    margin: 0;
  }
  button {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #aaa;
    cursor: pointer;
    &:hover {
      color: #000;
    }
  }
`;

const StyledModalBody = styled.div`
  padding: 0 30px 10px 30px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const StyledModalFooter = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 20px;
  flex-shrink: 0;
  button {
    padding: 8px 25px;
    background-color: #178b96;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    &:hover {
      background-color: #106271;
    }
  }
`;

// Modal-specific styled components
const ModalFilterWrapper = styled.div`
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;

  h6 {
    margin: 0 0 10px 0;
    color: #495057;
    font-weight: 600;
  }
`;

const ResultsInfo = styled.div`
  margin-bottom: 15px;
  color: #6c757d;
  font-size: 14px;
  font-style: italic;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin-bottom: 20px;
  max-height: 400px;
`;

const StyledTable = styled.table`
  width: 100%;
  font-family: "Space Grotesk", sans-serif;
  border-collapse: collapse;
  min-width: 600px;

  th,
  td {
    text-align: center;
    padding: 12px;
    border-bottom: 1px solid #dee2e6;
    white-space: nowrap;
  }

  thead th {
    background-color: #f2f6f7;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }
`;

const HoursBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  background-color: #17a2b8;
  color: white;
`;

const StyledPagination = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`;

const PaginationContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  button {
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    background: white;
    color: #178b96;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;

    &:hover:not(:disabled) {
      background-color: #f8f9fa;
    }

    &:disabled {
      color: #6c757d;
      cursor: not-allowed;
      opacity: 0.5;
    }

    &.active {
      background-color: #178b96;
      color: white;
      border-color: #178b96;
    }
  }
`;

// Search wrapper and input styles
const SearchWrapper = styled.div`
  margin-bottom: 20px;
`;

const SearchInputContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: end;
  align-items: center;
  max-width: 400px;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background-color: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #178b96;
    box-shadow: 0 0 0 3px rgba(23, 139, 150, 0.1);
  }

  &::placeholder {
    color: #6c757d;
    font-style: italic;
  }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    color: #dc3545;
  }

  &:focus {
    outline: none;
    background-color: #f8f9fa;
    color: #dc3545;
  }
`;
